import { FC, useMemo, type ReactNode } from 'react';
import { useSourceTypes } from '../../hooks/useSourceTypes';
import { PlugIcon } from 'lucide-react';

interface ConnectorIconProps {
  connectorType: string;
  size?: number;
  className?: string;
  /** Rendered when the icon is still loading or not found. Defaults to a generic plug icon. */
  fallback?: ReactNode;
}

/**
 * Displays the icon for a connector type (e.g., "plb_shopify") by looking up
 * the icon_path from source types. Unlike SourceIcon, this doesn't need a
 * source ID — just the connector type string.
 */
export const ConnectorIcon: FC<ConnectorIconProps> = ({ connectorType, size = 20, className = '', fallback }) => {
  const { data: sourceTypes } = useSourceTypes();

  const iconPath = useMemo(() => {
    if (!sourceTypes || !connectorType) return null;
    const sourceType = sourceTypes.find(st => st.id === connectorType);
    return sourceType?.icon_path || null;
  }, [sourceTypes, connectorType]);

  if (!iconPath) {
    return (fallback ?? <PlugIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`text-muted-foreground flex-shrink-0 ${className}`} />) as React.ReactElement;
  }

  return (
    <div
      className={`inline-flex items-center justify-center rounded-full overflow-hidden flex-shrink-0 ${className}`}
      style={{ width: size, height: size }}
    >
      <img
        src={iconPath}
        alt={connectorType}
        style={{ width: size, height: size, objectFit: 'cover' }}
        className="rounded-full"
      />
    </div>
  );
};
